import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Send auth cookies with every request
axios.defaults.withCredentials = true;

export const api = {
  // Health check
  health: () => axios.get(`${API}/health`),
  
  // Dashboard
  getDashboardSummary: () => axios.get(`${API}/dashboard/summary`),
  getDashboardAlerts: () => axios.get(`${API}/dashboard/alerts`),
  getExecutiveBriefing: (data) => axios.post(`${API}/dashboard/executive-briefing`, data),
  getRiskDeltas: () => axios.get(`${API}/dashboard/risk-deltas`),
  getRiskTrends: () => axios.get(`${API}/dashboard/trends`),
  getSectorRisks: () => axios.get(`${API}/dashboard/sector-risks`),
  
  // Countries
  getCountries: () => axios.get(`${API}/countries`),
  getCountryDetail: (code) => axios.get(`${API}/countries/${code}`),
  getCountrySignals: (code, limit = 15) => axios.get(`${API}/countries/${code}/signals?limit=${limit}`),
  
  // Sectors
  getSectors: () => axios.get(`${API}/sectors`),
  getSectorDetail: (name) => axios.get(`${API}/sectors/${encodeURIComponent(name)}`),
  
  // Reports
  getReports: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.report_type) params.append('report_type', filters.report_type);
    if (filters.country) params.append('country', filters.country);
    if (filters.audience) params.append('audience', filters.audience);
    if (filters.date_range) params.append('date_range', filters.date_range);
    if (filters.search) params.append('search', filters.search);
    if (filters.sort) params.append('sort', filters.sort);
    if (filters.page) params.append('page', filters.page);
    if (filters.page_size) params.append('page_size', filters.page_size);
    return axios.get(`${API}/reports?${params.toString()}`);
  },
  getReportStats: () => axios.get(`${API}/reports/stats`),
  getReportSchedule: () => axios.get(`${API}/reports/schedule`),
  getReportCountries: () => axios.get(`${API}/reports/countries`),
  generateReport: (data) => axios.post(`${API}/reports/generate`, data),
  getReportDetail: (id) => axios.get(`${API}/reports/${id}`),
  getPersonalisedReport: (id) => axios.get(`${API}/reports/${id}/personalised`),
  downloadReportPdf: (id) => `${API}/reports/${id}/pdf`,
  
  // Alerts
  getAlerts: (audience) => axios.get(`${API}/alerts${audience ? `?audience=${audience}` : ''}`),
  getTriggeredAlerts: () => axios.get(`${API}/alerts/triggered`),
  createAlert: (alert) => axios.post(`${API}/alerts`, alert),
  updateAlert: (id, isActive) => axios.put(`${API}/alerts/${id}?is_active=${isActive}`),
  deleteAlert: (id) => axios.delete(`${API}/alerts/${id}`),

  // Notifications
  getNotifications: () => axios.get(`${API}/notifications`),
  markNotificationsRead: (payload) => axios.post(`${API}/notifications/mark-read`, payload),

  // User preferences (server-side personalisation profile)
  getMyPreferences: () => axios.get(`${API}/user-profile/me`),
  saveMyPreferences: (profile) => axios.put(`${API}/user-profile/me`, profile),

  // Supplier risk timeline
  getSupplierRiskTimeline: (id, months = 6) => axios.get(`${API}/suppliers/${id}/risk-timeline?months=${months}`),

  // Historical trends
  getCountryHistory: (code, months = 12) => axios.get(`${API}/history/country/${code}?months=${months}`),
  compareCountryTrends: (countries, months = 12) => axios.get(`${API}/history/compare?countries=${countries}&months=${months}`),

  // Suppliers
  getSuppliers: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) qs.append(k, v); });
    return axios.get(`${API}/suppliers?${qs.toString()}`);
  },
  getSupplierSummary: () => axios.get(`${API}/suppliers/summary`),
  getSupplierDetail: (id) => axios.get(`${API}/suppliers/${id}`),
  createSupplier: (data) => axios.post(`${API}/suppliers`, data),
  updateSupplier: (id, data) => axios.put(`${API}/suppliers/${id}`, data),
  deleteSupplier: (id) => axios.delete(`${API}/suppliers/${id}`),
  deleteAllSuppliers: () => axios.delete(`${API}/suppliers`),
  uploadSuppliers: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/suppliers/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadSupplierTemplate: () => `${API}/suppliers/template`,
  exportSuppliersCsv: () => `${API}/suppliers/export/csv`,
  
  // Briefings
  getBriefings: (limit = 4) => axios.get(`${API}/briefings?limit=${limit}`),
  getBriefingDetail: (id) => axios.get(`${API}/briefings/${id}`),

  // Currency
  getCurrencyRates: () => axios.get(`${API}/currency/rates`),
  getCountryCurrency: (code) => axios.get(`${API}/currency/country/${code}`),

  // Disasters (GDACS)
  getActiveDisasters: () => axios.get(`${API}/disasters/active`),
  getCountryDisasters: (code) => axios.get(`${API}/disasters/country/${code}`),
  getDisasterEventDetail: (eventType, eventId) => axios.get(`${API}/disasters/event/${eventType}/${eventId}`),

  // Freight
  getFreightRates: () => axios.get(`${API}/freight/rates`),
  getCountryFreight: (code) => axios.get(`${API}/freight/country/${code}`),
  
  // User Profile
  createUserProfile: (profile) => axios.post(`${API}/user-profile`, profile),
  getUserProfile: (id) => axios.get(`${API}/user-profile/${id}`),
  
  // AI Generation
  generateNarrative: (countryCode) => axios.post(`${API}/generate-narrative?country_code=${countryCode}`),

  // Scenarios
  getScenarioTemplates: () => axios.get(`${API}/scenarios/templates`),
  runScenario: (data) => axios.post(`${API}/scenarios/run`, data),
  saveScenario: (data) => axios.post(`${API}/scenarios/save`, data),
  getSavedScenarios: () => axios.get(`${API}/scenarios`),
  deleteScenario: (id) => axios.delete(`${API}/scenarios/${id}`),

  // ABS Trade Data
  getABSTrade: (code) => axios.get(`${API}/abs/trade/${code}`),
  getABSTradeSnapshot: () => axios.get(`${API}/abs/trade-snapshot`),

  // DFAT Sanctions
  getSanctionsStatus: () => axios.get(`${API}/sanctions/status`),
  getSanctionsDashboard: () => axios.get(`${API}/sanctions/dashboard`),
  screenAllSuppliers: () => axios.get(`${API}/sanctions/screen-all`),
  screenSupplier: (data) => axios.post(`${API}/sanctions/screen`, data),

  // OEC Trade Structure
  getOECExports: (code, year = 2023) => axios.get(`${API}/oec/exports/${code}?year=${year}`),
  getOECAlternatives: (hsCode, exclude = '') => axios.get(`${API}/oec/alternatives?hs_code=${hsCode}&exclude=${exclude}`),
  getOECComplexity: (code) => axios.get(`${API}/oec/complexity/${code}`),

  // ILO Labour Data
  getILOLabour: (code) => axios.get(`${API}/ilo/labour/${code}`),
  getComparativeWages: () => axios.get(`${API}/ilo/wages/compare`),

  // Port Congestion
  getPortStatus: () => axios.get(`${API}/ports/status`),
  getPortDetail: (portId) => axios.get(`${API}/ports/${portId}`),

  // Analytics
  getConcentrationAnalysis: () => axios.get(`${API}/analytics/concentration`),
  getAukusCompliance: () => axios.get(`${API}/analytics/aukus`),
  getModernSlavery: () => axios.get(`${API}/analytics/modern-slavery`),
  getSourcingCompare: (country) => axios.get(`${API}/analytics/sourcing-compare?current_country=${country}`),

  // Watchlist & Signals
  getWatchlist: () => axios.get(`${API}/watchlist`),
  addToWatchlist: (item) => axios.post(`${API}/watchlist`, item),
  removeFromWatchlist: (id) => axios.delete(`${API}/watchlist/${id}`),
  getSignalsFeed: (limit = 50) => axios.get(`${API}/signals/feed?limit=${limit}`),

  // Sourcing
  getSourcingProducts: () => axios.get(`${API}/sourcing/products`),
  getSourcingAlternatives: (params) => axios.get(`${API}/sourcing/alternatives`, { params }),
  downloadSourcingPdf: (params) => `${API}/sourcing/pdf?${new URLSearchParams(params).toString()}`,

  // Ports & Routes
  getPortsRoutesPorts: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.region) qs.append('region', params.region);
    if (params.search) qs.append('search', params.search);
    return axios.get(`${API}/ports-routes/ports?${qs.toString()}`);
  },
  getPortsRoutesPortDetail: (portId, home = 'AU') => axios.get(`${API}/ports-routes/ports/${portId}?home=${home}`),
  getPortsRoutesRoutes: (home = 'AU') => axios.get(`${API}/ports-routes/routes?home=${home}`),
  getPortsRoutesRouteDetail: (routeId, home = 'AU') => axios.get(`${API}/ports-routes/routes/${routeId}?home=${home}`),
  getPortsRoutesChokepoints: () => axios.get(`${API}/ports-routes/chokepoints`),
  getPortsRoutesFreightHistory: (origin, dest) => axios.get(`${API}/ports-routes/freight-history/${origin}/${dest}`),
  getPortsRoutesSuggestions: (countries, home = 'AU') => axios.get(`${API}/ports-routes/suggestions?countries=${countries}&home=${home}`),
  getPortNarrative: (portId) => axios.get(`${API}/ports-routes/narrative/${portId}`),
  getRouteNarrative: (routeId, home = 'AU') => axios.get(`${API}/ports-routes/route-narrative/${routeId}?home=${home}`),
  getPortConditionsSummary: () => axios.get(`${API}/ports-routes/conditions-summary`),
  getWeatherEvents: () => axios.get(`${API}/ports-routes/weather`),
  getPortCustoms: (portId) => axios.get(`${API}/ports-routes/customs/${portId}`),
  getRouteCarriers: (routeId) => axios.get(`${API}/ports-routes/carriers/${routeId}`),
  getCountryPorts: (countryCode) => axios.get(`${API}/ports-routes/country-ports/${countryCode}`),
  getPortBriefingReport: () => axios.get(`${API}/ports-routes/briefing-report`),

  // Live data enrichment
  getSentimentTimeline: (code, timespan = '30d') => axios.get(`${API}/live/sentiment/${code}?timespan=${timespan}`),
  getMediaVolume: (code, timespan = '30d') => axios.get(`${API}/live/media-volume/${code}?timespan=${timespan}`),
  getSectorNews: (sector, max = 10) => axios.get(`${API}/live/sector-news/${sector}?max_results=${max}`),
  getDisasterDetail: (type, id) => axios.get(`${API}/live/disaster/${type}/${id}`),
  getEconomicIndicators: (code) => axios.get(`${API}/live/economic-indicators/${code}`),
  getTradeProducts: (code) => axios.get(`${API}/live/trade-products/${code}`),
  getOECAlternatives: (hsCode, exclude = '') => axios.get(`${API}/live/oec/alternatives/${hsCode}?exclude=${exclude}`),
};

export default api;
